import { useMemo } from 'react';

import { AdminOrganizationListStreamSortBy } from 'protocol/api/core/svc_adminka_pb';
import { Organization } from 'protocol/model/dto_organization_pb';

import { useDebounce } from '@/shared/hooks';
import { normalizeEmail } from '@/shared/lib';

import { useOrganizationListQuery } from './useOrganizationListQuery';

type UseOrganizationsByOwnerEmailParams = {
  email: string;
  enabled?: boolean;
};

type UseOrganizationsByOwnerEmailResult = {
  // `checking` also covers the debounce window, so a caller that gates a submit
  // on it never lets a stale "nothing found" through while typing.
  checking: boolean;
  hasError: boolean;
  organizations: Organization[];
};

/**
 * Looks up organizations whose owner email matches exactly.
 *
 * `AdminOrganizationListStream` only offers a substring search over name and
 * owner email, so the exact match is done here over the returned page. The
 * listing is permission-scoped: a distributor user sees only that distributor's
 * clients and will not find organizations outside of it.
 */
export const useOrganizationsByOwnerEmail = (
  params: UseOrganizationsByOwnerEmailParams,
): UseOrganizationsByOwnerEmailResult => {
  const { email, enabled = true } = params;

  const normalizedEmail = normalizeEmail(email);
  const debouncedEmail = useDebounce(normalizedEmail);

  const isEnabled = enabled && Boolean(debouncedEmail);

  const { hasError, loading, organizations } = useOrganizationListQuery({
    enabled: isEnabled,
    search: debouncedEmail,
    sortBy:
      AdminOrganizationListStreamSortBy.AdminOrganizationListStreamSortByOwnerEmail,
  });

  const matchedOrganizations = useMemo(() => {
    if (!isEnabled) {
      return [];
    }

    return organizations.filter(
      (organization) =>
        normalizeEmail(organization.OwnerEmail ?? '') === debouncedEmail,
    );
  }, [debouncedEmail, isEnabled, organizations]);

  return {
    checking:
      enabled &&
      Boolean(normalizedEmail) &&
      (loading || debouncedEmail !== normalizedEmail),
    hasError,
    organizations: matchedOrganizations,
  };
};
